// src/pages/admin/ProjectDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProject, deleteProject } from '../../utils/api';
import AdminSidebar from '../../components/admin/AdminSidebar';
import Loading from '../../components/common/Loading';
import toast from 'react-hot-toast';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaTrash, 
  FaCalendar, 
  FaMapMarker, 
  FaBuilding,
  FaEye,
  FaImages,
  FaInfoCircle,
  FaChartBar,
  FaCogs,
  FaLightbulb,
  FaCheckCircle,
  FaStar
} from 'react-icons/fa';

const AdminProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const data = await getProject(id);
      setProject(data);
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await deleteProject(id);
        toast.success('Project deleted successfully');
        navigate('/admin/projects');
      } catch (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8">
          <Loading fullScreen message="Loading project details..." />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Project Not Found</h2>
            <Link to="/admin/projects" className="text-blue-600 hover:text-blue-800">
              ← Back to Projects
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar />
      <div className="flex-1 p-8">
        {/* ADD THIS HEADER SECTION - Same as Projects.jsx */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Project Details</h1>
              <p className="text-gray-600 mt-2">
                View and manage details for: <span className="font-semibold text-gray-800">{project.title}</span>
              </p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/admin/projects"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FaArrowLeft className="h-4 w-4 mr-2" />
                Back to Projects
              </Link>
              <Link
                to={`/admin/projects/edit/${project.id}`}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FaEdit className="h-4 w-4 mr-2" />
                Edit Project
              </Link>
            </div>
          </div>
        </div>
        {/* END OF HEADER SECTION */}

        {/* Rest of your existing ProjectDetail content */}
        <div className="bg-white rounded-xl shadow-lg">
          {/* Project Info Card */}
          <div className="p-6 border-b">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">{project.title}</h2>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    project.status === 'completed' ? 'bg-green-100 text-green-800' :
                    project.status === 'ongoing' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {project.status}
                  </span>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    project.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {project.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {project.is_featured && (
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Featured
                    </span>
                  )}
                </div>
                
                <p className="text-gray-600 mb-6">{project.description}</p>

                {/* Project Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center text-sm text-gray-500 mb-1">
                      <FaBuilding className="h-4 w-4 mr-2" />
                      Category
                    </div>
                    <div className="font-semibold text-gray-800">{project.category || 'General'}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center text-sm text-gray-500 mb-1">
                      <FaBuilding className="h-4 w-4 mr-2" />
                      Client
                    </div>
                    <div className="font-semibold text-gray-800">{project.client_name || 'N/A'}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center text-sm text-gray-500 mb-1">
                      <FaMapMarker className="h-4 w-4 mr-2" />
                      Location
                    </div>
                    <div className="font-semibold text-gray-800">{project.location || 'N/A'}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center text-sm text-gray-500 mb-1">
                      <FaCalendar className="h-4 w-4 mr-2" />
                      Duration
                    </div>
                    <div className="font-semibold text-gray-800">
                      {project.start_date ? formatDate(project.start_date) : 'N/A'} - 
                      {project.end_date ? formatDate(project.end_date) : 'Ongoing'}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/projects/${project.id}`}
                    target="_blank"
                    className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <FaEye className="h-4 w-4 mr-2" />
                    View Public Page
                  </Link>
                  <button className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                    <FaEdit className="h-4 w-4 mr-2" />
                    Edit Images
                  </button>
                  <button className="inline-flex items-center px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors">
                    <FaCheckCircle className="h-4 w-4 mr-2" />
                    Toggle Active
                  </button>
                  <button className="inline-flex items-center px-4 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors">
                    <FaStar className="h-4 w-4 mr-2" />
                    Toggle Featured
                  </button>
                </div>
              </div>

              {project.images && project.images[0] && (
                <div className="md:w-64">
                  <img 
                    src={project.images[0]} 
                    alt={project.title}
                    className="w-full h-48 object-cover rounded-lg shadow"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="border-b">
            <div className="flex space-x-1 px-6">
              {['overview', 'details', 'gallery', 'analytics'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'overview' && <FaInfoCircle className="inline mr-2 h-4 w-4" />}
                  {tab === 'details' && <FaCogs className="inline mr-2 h-4 w-4" />}
                  {tab === 'gallery' && <FaImages className="inline mr-2 h-4 w-4" />}
                  {tab === 'analytics' && <FaChartBar className="inline mr-2 h-4 w-4" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* System Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">System Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Created</div>
                      <div className="font-medium text-gray-800">
                        {project.created_at ? new Date(project.created_at).toLocaleString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Last Updated</div>
                      <div className="font-medium text-gray-800">
                        {project.updated_at ? new Date(project.updated_at).toLocaleString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Images Count</div>
                      <div className="font-medium text-gray-800">{project.images?.length || 0}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Videos Count</div>
                      <div className="font-medium text-gray-800">{project.videos?.length || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="border border-red-200 rounded-lg p-6 bg-red-50">
                  <h3 className="text-lg font-bold text-red-800 mb-2">Danger Zone</h3>
                  <p className="text-red-600 mb-4">These actions are irreversible. Please be cautious.</p>
                  <button
                    onClick={handleDelete}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <FaTrash className="h-4 w-4 mr-2" />
                    Delete Project
                  </button>
                </div>
              </div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && project.details && (
              <div className="space-y-6">
                {/* Project Scope */}
                {project.details.scope && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Project Scope</h3>
                    <div className="prose max-w-none">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-600 whitespace-pre-line">{project.details.scope}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Challenges */}
                {project.details.challenges && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Challenges Faced</h3>
                    <div className="prose max-w-none">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-600 whitespace-pre-line">{project.details.challenges}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Solutions */}
                {project.details.solutions && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Solutions Implemented</h3>
                    <div className="prose max-w-none">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-600 whitespace-pre-line">{project.details.solutions}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Results */}
                {project.details.results && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Results & Achievements</h3>
                    <div className="prose max-w-none">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-600 whitespace-pre-line">{project.details.results}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="space-y-6">
                {/* Images */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Project Images ({project.images?.length || 0})</h3>
                  {project.images && project.images.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {project.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={image} 
                            alt={`${project.title} - Image ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex space-x-2">
                              <button className="p-2 bg-white rounded-full hover:bg-gray-100">
                                <FaEye className="h-4 w-4 text-gray-700" />
                              </button>
                              <button className="p-2 bg-white rounded-full hover:bg-gray-100">
                                <FaTrash className="h-4 w-4 text-red-600" />
                              </button>
                            </div>
                          </div>
                          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      No images uploaded for this project
                    </div>
                  )}
                </div>

                {/* Videos */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Project Videos ({project.videos?.length || 0})</h3>
                  {project.videos && project.videos.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.videos.map((video, index) => (
                        <div key={index} className="relative bg-gray-100 h-48 rounded-lg flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-gray-400 mb-2">Video {index + 1}</div>
                            <a 
                              href={video} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View Video Link
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      No videos uploaded for this project
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="text-center py-12">
                <div className="inline-block p-8 bg-gray-50 rounded-lg">
                  <FaChartBar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Analytics Coming Soon</h3>
                  <p className="text-gray-600">Project analytics features are currently in development.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProjectDetail;